<template>
  <!-- prettier-ignore -->
  <div
    class="PartnerWidget"
    :class="classes"
  >
    <component
      :is="views[screen]"
      v-if="ready && languageLoaded"
      class="PartnerWidget-container"
      v-bind="screenProps"
      v-on="screenEvents"
    />

    <loading-alt v-else />

    <div id="modals" />

    <notifications />
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'

import Error from '@/modules/PartnerWidget/views/Error'
import Entry from '@/modules/PartnerWidget/views/Entry'
import Bundle from '@/modules/PartnerWidget/views/Bundle'
import Detail from '@/modules/PartnerWidget/views/Detail'

import LoadingAlt from '@/components/Container/LoadingAlt'
import Notifications from '@/components/Container/Notifications'

import usePartnerWidget from '@/hooks/usePartnerWidget'
import usePartnerWidgetBundleLoader from '@/hooks/usePartnerWidgetBundleLoader'

// Props
const props = defineProps({
  bundleId: {
    type: String,
    default: '',
  },
  language: {
    type: String,
    default: 'de',
  },
  theme: {
    type: String,
    default: '',
  },
  displayMode: {
    type: String,
    default: '',
  },
})

// Hooks
const { getQueryParams, partnerWidgetDisplayMode, partnerWidgetTheme } = usePartnerWidget()
const {
  loadBundle,
  languageLoaded,
  ready,
  screen,
  screensDefinition,
  screensDefinitionInit,
  // Setters
  setBundleId,
  setDisplayMode,
  setLanguage,
  setTheme,
} = usePartnerWidgetBundleLoader()

// Init
const views = {
  Bundle,
  Detail,
  Error,
  Entry,
}

// Data
const screenEvents = ref({})
const screenProps = ref({})

// Computed
const classes = computed(() => {
  return [`theme-${partnerWidgetTheme.value}`, partnerWidgetDisplayMode.value, `screen-${screen.value}`]
})

// Methods
function init() {
  setBundleId(props.bundleId)
  setDisplayMode(props.displayMode)
  setLanguage(props.language)
  setTheme(props.theme)
}
init()

// Lifecycle hooks
onMounted(() => {
  screensDefinitionInit()
  loadBundle(getQueryParams())
})

// Watchers
watch(
  () => screen,
  () => {
    if (screen.value) {
      screenEvents.value = screensDefinition.value[screen.value].events
      screenProps.value = screensDefinition.value[screen.value].props
    }
  },
  { deep: true }
)

watch(
  () => screensDefinition,
  () => {
    screenEvents.value = screensDefinition.value[screen.value].events
    screenProps.value = screensDefinition.value[screen.value].props
  },
  { deep: true }
)

// Props watchers
watch(
  () => props.bundleId,
  value => {
    if (value) {
      setBundleId(value)
    }
  }
)

watch(
  () => props.displayMode,
  value => {
    if (value) {
      setDisplayMode(value)
    }
  }
)

watch(
  () => props.language,
  value => {
    if (value) {
      setLanguage(value)
    }
  }
)

watch(
  () => props.theme,
  value => {
    if (value) {
      setTheme(value)
    }
  }
)
</script>

<style>
* {
  box-sizing: border-box;
}

.global-sanitas-link {
  font-family: 'Open Sans', sans-serif;
  font-weight: 700;
  font-style: normal;
  line-height: 1.1;
  color: var(--c-original-green);
  cursor: pointer;
  transition: color 0.3s ease-in-out 0s;
  display: inline;

  border-bottom: none; /* overwriting global leaking styles */

  &.emphasized {
    position: relative;
    padding-left: 20px;

    &:before {
      content: '→';
      position: absolute;
      left: 0;
      top: 2px;
    }

    &.back {
      &:before {
        content: '←';
        position: absolute;
        left: 0;
        top: 2px;
      }
    }
  }

  &:hover {
    color: var(--c-hover-original-green);
    text-decoration: underline;
    border-bottom: none; /* overwriting global leaking styles */
  }
}

.tippy-box {
  border-radius: 0;
  background-color: var(--c-black);

  .tippy-content {
    padding: 15px;

    ul {
      padding-left: 40px;

      li {
        color: var(--c-white);
      }
    }
  }
}

.PartnerWidget {
  font-family: 'Open Sans', sans-serif;
  margin: 0 auto;
  padding: var(--pw-page-padding);
  position: relative;

  h1 {
    font-size: var(--pw-font-size-heading);
    line-height: var(--pw-line-height-heading);
    color: var(--c-original-green);
  }

  em {
    font-style: normal;
    color: var(--c-sanitas-green);
  }

  /* displayMode dependant styling */

  &.narrow {
    width: 300px;
    height: 600px;

    &.screen-Entry {
      padding: 30px;
    }

    @media screen and (max-width: 299px) {
      min-width: 200px;
      width: 100vw;

      &.screen-Entry {
        padding: 20px;
      }
    }
  }

  &.wide {
    width: 980px;
    height: 100vw;
    min-height: 300px;
    max-height: 600px;
    padding: 15px;

    &.screen-Entry {
      padding: 35px;
    }
  }
}

@media screen and (max-width: 299px) {
  .PartnerWidget .sanitas-button {
    font-size: var(--pw-font-size-small);
  }
}
</style>

<style name="short">
.PartnerWidget.short {
  height: 100vh;
  max-width: 992px;

  &.screen-Entry {
    padding: 20px;
  }
}
</style>
